import { RootState } from "app/store";
import {
  DeleteCar,
  GetAdminCar,
  RepostCar,
  UpdateCar,
} from "api/services/car";
import { getCarMakes } from "api/services/dealer";
import {
  getExternalImportSource,
  ExternalImportSource,
} from "api/services/externalImports";
import { GetAgents } from "api/services/user";
import InspectionEditor from "components/car/InspectionEditor";
import CarPhotoManager, {
  CarPhotoItem,
} from "components/car/CarPhotoManager";
import Menu from "components/common/Menu";
import { SideBarTabs } from "constants/tabs";
import { useTabActive } from "hook/useTabActive";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import {
  IoArrowBack,
  IoCallOutline,
  IoCarSportOutline,
  IoCheckmarkCircle,
  IoChevronDown,
  IoOpenOutline,
  IoPersonOutline,
  IoRefreshOutline,
  IoSaveOutline,
  IoShareSocialOutline,
  IoSparklesOutline,
  IoTrashOutline,
  IoWarningOutline,
} from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { setCar } from "reducers/carReducer";
import { setCarUpdateReload } from "reducers/reloadReducer";
import { Paths } from "router/Paths";
import { CommonStyles } from "styles/CommonStyles";
import { CarMakeType, CarType, UserType } from "types/loadedData";
import {
  dashboardConfirm,
  localizeDashboardError,
  translateDashboardText,
} from "i18n/dashboardLanguage";

type EditorForm = {
  make: string;
  model: string;
  year: string;
  price: string;
  listingType: "Sale" | "Rent";
  usageType: "new" | "used";
  bodyType: string;
  fuelType: string;
  transmissionType: string;
  mileage: string;
  batteryRangeKm: string;
  color: string;
  location: string;
  description: string;
  phone: string;
  ownerPhone: string;
  brokerCount: string;
  withDriver: boolean;
  status: string;
  assigned: string;
  buyerName: string;
  buyerPhone: string;
  isDeal: boolean;
  actualSalePrice: string;
  actualSoldAt: string;
  outcomeVerified: boolean;
};

type FormErrors = Partial<Record<keyof EditorForm | "photos", string>>;

const EMPTY_FORM: EditorForm = {
  make: "",
  model: "",
  year: "",
  price: "",
  listingType: "Sale",
  usageType: "used",
  bodyType: "",
  fuelType: "",
  transmissionType: "",
  mileage: "",
  batteryRangeKm: "",
  color: "",
  location: "Addis Ababa",
  description: "",
  phone: "",
  ownerPhone: "",
  brokerCount: "",
  withDriver: false,
  status: "Active",
  assigned: "",
  buyerName: "",
  buyerPhone: "",
  isDeal: false,
  actualSalePrice: "",
  actualSoldAt: new Date().toISOString().slice(0, 10),
  outcomeVerified: false,
};

const FUEL_OPTIONS = ["petrol", "diesel", "hybrid", "electric"];
const TRANSMISSION_OPTIONS = ["automatic", "manual", "semi-Automatic"];
const STATUS_OPTIONS = ["Active", "Assigned", "Rented", "Sold", "Cancelled"];
const MAX_YEAR = new Date().getFullYear() + 1;

const normalizeChoice = (value: string | undefined, options: string[]) => {
  const raw = String(value || "");
  return options.find((option) => option.toLowerCase() === raw.toLowerCase()) || raw;
};

const digitsOnly = (value: unknown) => String(value ?? "").replace(/\D/g, "");

const makeId = (make?: CarMakeType | string) =>
  typeof make === "object" ? make?._id || "" : String(make || "");

const makeName = (make?: CarMakeType | string) =>
  typeof make === "object" ? make?.name || "" : "";

const toForm = (car: CarType): EditorForm => ({
  make: makeId(car.make),
  model: car.model || "",
  year: String(car.year || ""),
  price: String(car.price || ""),
  listingType: car.listingType === "Rent" ? "Rent" : "Sale",
  usageType: car.usageType === "new" ? "new" : "used",
  bodyType: car.bodyType || "",
  fuelType: normalizeChoice(car.fuelType, FUEL_OPTIONS),
  brokerCount: car.brokerCount == null ? "" : String(car.brokerCount),
  transmissionType: normalizeChoice(car.transmissionType, TRANSMISSION_OPTIONS),
  mileage: car.mileage === undefined || car.mileage === null ? "" : String(car.mileage),
  batteryRangeKm:
    car.batteryRangeKm === undefined || car.batteryRangeKm === null
      ? ""
      : String(car.batteryRangeKm),
  color: car.color || "",
  location: car.location || "Addis Ababa",
  description: car.description || "",
  phone: car.phone || "",
  ownerPhone: car.ownerPhone || "",
  withDriver: Boolean(car.withDriver),
  status: String(car.status || "Active"),
  assigned: car.assigned?._id || "",
  buyerName: car.buyerName || "",
  buyerPhone: car.buyerPhone || "",
  isDeal: Boolean(car.isDeal),
  actualSalePrice: String(car.actualSalePrice || ""),
  actualSoldAt: car.soldAt?.slice(0, 10) || new Date().toISOString().slice(0, 10),
  outcomeVerified: Boolean(car.saleVerifiedAt),
});

const toPhotos = (car: CarType): CarPhotoItem[] =>
  (car.images || []).map((url, index) => ({
    id: `remote-${index}-${url}`,
    previewUrl: url,
    remoteUrl: url,
  }));

const snapshot = (form: EditorForm, photos: CarPhotoItem[]) =>
  JSON.stringify({
    form,
    photos: photos.map((photo) =>
      photo.remoteUrl || `${photo.file?.name}:${photo.file?.size}:${photo.file?.lastModified}`
    ),
  });

const formatDate = (value?: string) => {
  if (!value) return "Not available";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Not available"
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

const responseError = (response: any, fallback: string) =>
  response?.error || response?.message || response?.msg || fallback;

const hasRestrictedDescription = (value: string) => {
  const email = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;
  const telegram = /(?:\btelegram\b|\bt\.me\/\S*|\btelegram\.me\/\S*|(?:^|\s)@[A-Z0-9_]{3,}\b)/i;
  const link = /(?:\bhttps?:\/\/\S+|\bwww\.\S+|\b(?:[A-Z0-9-]+\.)+(?:com|net|org|io|me|co|et|app|site|online|link)\b(?:\/\S*)?)/i;
  const phones = value.match(/(?:\+?\d[\d\s().-]{7,}\d)/g) || [];
  return email.test(value) || telegram.test(value) || link.test(value) || phones.length > 0;
};

function FieldError({ message }: { message?: string }) {
  return message ? <span className={ClassName.fieldError}>{message}</span> : null;
}

function CarPage() {
  useTabActive(SideBarTabs.Cars.name);
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cachedCar = useSelector((state: RootState) => state.car.car);
  const dashboardUser = useSelector((state: RootState) => state.user.user);
  const isAdmin = dashboardUser?.role === "Admin";

  const [form, setForm] = useState<EditorForm>(EMPTY_FORM);
  const [photos, setPhotos] = useState<CarPhotoItem[]>([]);
  const [carMakes, setCarMakes] = useState<CarMakeType[]>([]);
  const [agents, setAgents] = useState<UserType[]>([]);
  const [activeCar, setActiveCar] = useState<CarType | null>(
    cachedCar?._id === id ? cachedCar : null
  );
  const [initialSnapshot, setInitialSnapshot] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [pageLoading, setPageLoading] = useState(!activeCar);
  const [pageError, setPageError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reposting, setReposting] = useState(false);
  const [source, setSource] = useState<ExternalImportSource | null>(null);

  const minimumImages = activeCar?.minimumImages ||
    (activeCar?.sourceProvider ? 1 : 4);
  const isElectric = form.fuelType.toLowerCase() === "electric";
  const isUsedCar = form.usageType === "used";
  const currentSnapshot = useMemo(() => snapshot(form, photos), [form, photos]);
  const isDirty = Boolean(initialSnapshot && currentSnapshot !== initialSnapshot);
  const lastRepostedAt = activeCar?.lastRepostedAt
    ? new Date(activeCar.lastRepostedAt).getTime()
    : 0;
  const repostAvailableAt = lastRepostedAt + 48 * 60 * 60 * 1000;
  const repostOnCooldown = Boolean(lastRepostedAt && Date.now() < repostAvailableAt);
  const canRepost = Boolean(
    activeCar && !["Sold", "Rented", "Cancelled"].includes(String(activeCar.status))
  );

  const hydrate = (car: CarType) => {
    const nextForm = toForm(car);
    const nextPhotos = toPhotos(car);
    setActiveCar(car);
    setForm(nextForm);
    setPhotos(nextPhotos);
    setInitialSnapshot(snapshot(nextForm, nextPhotos));
    setErrors({});
  };

  useEffect(() => {
    let mounted = true;
    if (!id) {
      setPageError("Car ID is missing.");
      setPageLoading(false);
      return;
    }

    setPageLoading(!activeCar);
    setPageError("");
    Promise.all([
      GetAdminCar({ id }),
      getCarMakes(),
      GetAgents({ limit: 100, offset: 0 }),
    ])
      .then(([carResponse, makes, agentResponse]) => {
        if (!mounted) return;
        if (carResponse.status !== 200 || !carResponse.car) {
          throw new Error(carResponse.message || "Car could not be loaded.");
        }
        hydrate(carResponse.car);
        dispatch(setCar(carResponse.car));
        setCarMakes(makes);
        setAgents(agentResponse.status === 200 ? agentResponse.agents || [] : []);
      })
      .catch((error) => {
        if (mounted) setPageError(error?.message || "Car could not be loaded.");
      })
      .finally(() => {
        if (mounted) setPageLoading(false);
      });

    return () => {
      mounted = false;
    };
    // The route id is the authoritative load key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch]);

  useEffect(() => {
    if (!activeCar?._id || !activeCar.sourceProvider) {
      setSource(null);
      return;
    }
    getExternalImportSource(String(activeCar._id))
      .then(setSource)
      .catch(() => setSource(null));
  }, [activeCar?._id, activeCar?.sourceProvider]);

  useEffect(() => {
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirty || saving) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [isDirty, saving]);

  const updateField = <K extends keyof EditorForm>(key: K, value: EditorForm[K]) => {
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === "listingType" && value === "Rent") {
        next.usageType = "used";
      }
      if (key === "fuelType" && String(value).toLowerCase() !== "electric") {
        next.batteryRangeKm = "";
      }
      return next;
    });
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const handlePhotosChange = (next: CarPhotoItem[]) => {
    setPhotos(next);
    setErrors((current) => ({ ...current, photos: undefined }));
  };

  const validate = () => {
    const next: FormErrors = {};
    const year = Number(form.year);
    const price = Number(form.price);
    const phoneDigits = digitsOnly(form.ownerPhone);
    const buyerPhoneDigits = digitsOnly(form.buyerPhone);

    if (photos.length < minimumImages || photos.length > 10) {
      next.photos = `Keep between ${minimumImages} and 10 photos.`;
    }
    if (!form.make) next.make = "Select a car brand.";
    if (!form.model.trim()) next.model = "Enter the car model.";
    if (!/^\d{4}$/.test(form.year) || year < 1900 || year > MAX_YEAR) {
      next.year = `Enter a year between 1900 and ${MAX_YEAR}.`;
    }
    if (!Number.isFinite(price) || price <= 0) next.price = "Enter a valid price.";
    if (form.status === "Sold") {
      const finalPrice = Number(form.actualSalePrice);
      if (!Number.isFinite(finalPrice) || finalPrice <= 0) {
        next.actualSalePrice = "Enter the actual final selling price.";
      }
      if (!form.actualSoldAt) next.actualSoldAt = "Enter the date the car was sold.";
    }
    if (!form.fuelType) next.fuelType = "Select a fuel type.";
    if (!form.description.trim()) next.description = "Enter a short description.";
    else if (hasRestrictedDescription(form.description)) {
      next.description =
        "Description cannot contain phone numbers, email, Telegram, or links.";
    }
    if (isAdmin && form.ownerPhone && phoneDigits.length < 6) {
      next.ownerPhone = "Owner phone must contain at least 6 digits.";
    }
    if (form.buyerPhone && buyerPhoneDigits.length < 9) {
      next.buyerPhone = "Enter a valid buyer phone number.";
    }
    if (
      isElectric &&
      form.batteryRangeKm &&
      Number(form.batteryRangeKm) <= 0
    ) {
      next.batteryRangeKm = "Enter a valid electric range.";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    if (!activeCar || saving || !validate()) {
      requestAnimationFrame(() =>
        document.getElementById("car-editor-errors")?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      );
      return;
    }

    setSaving(true);
    try {
      const response = await UpdateCar({
        id: String(activeCar._id),
        description: form.description.trim(),
        color: form.color.trim(),
        location: form.location.trim(),
        fuelType: form.fuelType,
        brokerCount: isAdmin ? form.brokerCount : undefined,
        batteryRangeKm: isElectric ? form.batteryRangeKm : "",
        make: form.make,
        mileage: form.mileage || "0",
        model: form.model.trim(),
        phone: form.ownerPhone.trim(),
        ownerPhone: form.ownerPhone.trim(),
        price: form.price,
        transmissionType: form.transmissionType,
        usageType: form.listingType === "Rent" ? "used" : form.usageType,
        year: form.year,
        listingType: form.listingType,
        withDriver:
          form.listingType === "Rent" ? String(form.withDriver) : undefined,
        assigned: form.assigned,
        buyerName: form.buyerName.trim(),
        buyerPhone: form.buyerPhone.trim(),
        status: form.status,
        isDeal: form.isDeal,
        actualSalePrice: form.status === "Sold" ? form.actualSalePrice : undefined,
        actualSoldAt: form.status === "Sold" ? form.actualSoldAt : undefined,
        outcomeVerified: form.status === "Sold" ? form.outcomeVerified : undefined,
        existingImages: photos.map((photo, index) => ({
          key: index,
          image: photo.remoteUrl || "",
        })),
        images: photos.flatMap((photo) => (photo.file ? [photo.file] : [])),
        stayOnPage: true,
      });

      if (response.status !== 200 || !response.car) {
        throw new Error(responseError(response, "Car could not be updated."));
      }

      hydrate(response.car);
      dispatch(setCar(response.car));
      dispatch(setCarUpdateReload(true));
      enqueueSnackbar(translateDashboardText("Car changes saved"), {
        variant: "success",
      });
    } catch (error: any) {
      const message = error?.message || "Car could not be updated.";
      setErrors((current) => ({ ...current, description: current.description }));
      enqueueSnackbar(localizeDashboardError(message), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => {
    if (isDirty && !dashboardConfirm("Discard unsaved changes?")) return;
    navigate(-1);
  };

  const repost = async () => {
    if (!activeCar || reposting || repostOnCooldown) return;
    setReposting(true);
    try {
      const response = await RepostCar({ id: String(activeCar._id) });
      if (response.status !== 200 || !response.car) {
        throw new Error(responseError(response, "Car could not be reposted."));
      }
      hydrate(response.car);
      dispatch(setCar(response.car));
      dispatch(setCarUpdateReload(true));
      enqueueSnackbar(translateDashboardText("Car moved to the top"), {
        variant: "success",
      });
    } catch (error: any) {
      enqueueSnackbar(localizeDashboardError(error?.message || "Car could not be reposted."), {
        variant: "error",
      });
    } finally {
      setReposting(false);
    }
  };

  const deleteListing = async () => {
    if (!activeCar || deleting) return;
    if (!dashboardConfirm("Permanently delete this listing and its photos?")) return;
    setDeleting(true);
    try {
      const response = await DeleteCar({ id: String(activeCar._id) });
      if (response.status !== 200) {
        throw new Error(responseError(response, "Car could not be deleted."));
      }
      dispatch(setCar(null));
      dispatch(setCarUpdateReload(true));
      enqueueSnackbar(translateDashboardText("Car deleted"), {
        variant: "success",
      });
      navigate(Paths.cars);
    } catch (error: any) {
      enqueueSnackbar(localizeDashboardError(error?.message || "Car could not be deleted."), {
        variant: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  if (pageLoading) {
    return (
      <div className={CommonStyles.pageWrapper}>
        <Menu />
        <div className={ClassName.loadingShell}>
          <div className="loader" />
          <strong>Loading car details...</strong>
        </div>
      </div>
    );
  }

  if (pageError || !activeCar) {
    return (
      <div className={CommonStyles.pageWrapper}>
        <Menu />
        <div className={ClassName.loadingShell}>
          <IoWarningOutline size={34} className="text-primary" />
          <strong>{pageError || "Car was not found."}</strong>
          <button type="button" className={ClassName.darkButton} onClick={() => navigate(Paths.cars)}>
            Return to cars
          </button>
        </div>
      </div>
    );
  }

  const selectedMake = carMakes.find((make) => make._id === form.make);
  const owner = activeCar.owner;

  return (
    <div className={`${CommonStyles.pageWrapper} ${ClassName.page}`}>
      <Menu />

      <header className={ClassName.pageHeader}>
        <button type="button" className={ClassName.backButton} onClick={goBack}>
          <IoArrowBack size={20} />
        </button>
        <div className="min-w-0 flex-1">
          <div className={ClassName.headerMeta}>
            <span>{activeCar.tag || "CAR"}</span>
            <span className={ClassName.statusBadge}>{form.status}</span>
            {isDirty ? <span className={ClassName.unsavedBadge}>Unsaved changes</span> : null}
          </div>
          <h1 className={ClassName.pageTitle}>Edit car listing</h1>
          <p className={ClassName.pageSubtitle}>
            Update the public listing and internal sales details in one place.
          </p>
        </div>
        <div className={ClassName.headerActions}>
          <button
            type="button"
            className={ClassName.distributeButton}
            onClick={() => navigate(`${Paths.distribution}?car=${activeCar._id}`)}
          >
            <IoShareSocialOutline size={18} />
            Distribute
          </button>
          <button
            type="button"
            className={ClassName.headerSave}
            disabled={saving || !isDirty}
            onClick={save}
          >
            <IoSaveOutline size={18} />
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </header>

      <div id="car-editor-errors" aria-live="polite">
        {Object.keys(errors).length ? (
          <div className={ClassName.errorSummary}>
            <IoWarningOutline size={20} />
            <span>Please fix the highlighted listing details before saving.</span>
          </div>
        ) : null}
      </div>

      <main className={ClassName.layout}>
        <div className={ClassName.mainColumn}>
          <section className={ClassName.card}>
            <div className={ClassName.sectionHeader}>
              <div>
                <span className={ClassName.eyebrow}>LISTING SETUP</span>
                <h2 className={ClassName.sectionTitle}>Type and condition</h2>
              </div>
              <IoCarSportOutline size={24} className="text-slate-400" />
            </div>

            <div className={ClassName.segmented}>
              {(["Sale", "Rent"] as const).map((value) => (
                <button
                  type="button"
                  key={value}
                  className={form.listingType === value ? ClassName.segmentActive : ClassName.segment}
                  onClick={() => updateField("listingType", value)}
                >
                  {value}
                </button>
              ))}
            </div>

            {form.listingType === "Sale" ? (
              <div className={ClassName.conditionRow}>
                {([
                  { value: "used", label: "Used car", icon: IoCarSportOutline },
                  { value: "new", label: "New car", icon: IoSparklesOutline },
                ] as const).map((item) => {
                  const Icon = item.icon;
                  const active = form.usageType === item.value;
                  return (
                    <button
                      type="button"
                      key={item.value}
                      className={active ? ClassName.conditionActive : ClassName.condition}
                      onClick={() => updateField("usageType", item.value)}
                    >
                      <Icon size={17} /> {item.label}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className={ClassName.driverRow}>
                <div>
                  <strong>Driver option</strong>
                  <span>Choose how this rental is offered.</span>
                </div>
                <div className={ClassName.miniSegmented}>
                  {[false, true].map((value) => (
                    <button
                      type="button"
                      key={String(value)}
                      className={form.withDriver === value ? ClassName.miniActive : ClassName.miniButton}
                      onClick={() => updateField("withDriver", value)}
                    >
                      {value ? "With driver" : "No driver"}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>

          <CarPhotoManager
            photos={photos}
            minimum={minimumImages}
            error={errors.photos}
            onChange={handlePhotosChange}
          />

          <section className={ClassName.card}>
            <div className={ClassName.sectionHeader}>
              <div>
                <span className={ClassName.eyebrow}>PUBLIC INFORMATION</span>
                <h2 className={ClassName.sectionTitle}>Car details</h2>
                <p className={ClassName.sectionHelper}>These details appear in the app and web listing.</p>
              </div>
            </div>

            <div className={ClassName.formGrid}>
              <label className={ClassName.field}>
                <span>Brand *</span>
                <select
                  value={form.make}
                  onChange={(event) => updateField("make", event.target.value)}
                  className={`${ClassName.input} ${errors.make ? ClassName.inputError : ""}`}
                >
                  <option value="">Select brand</option>
                  {form.make && !carMakes.some((make) => make._id === form.make) ? (
                    <option value={form.make}>{makeName(activeCar.make) || "Current brand"}</option>
                  ) : null}
                  {carMakes.map((make) => (
                    <option key={make._id} value={make._id}>{make.name}</option>
                  ))}
                </select>
                <FieldError message={errors.make} />
              </label>

              <label className={ClassName.field}>
                <span>Model *</span>
                <input
                  value={form.model}
                  onChange={(event) => updateField("model", event.target.value)}
                  placeholder="e.g. Corolla Cross"
                  className={`${ClassName.input} ${errors.model ? ClassName.inputError : ""}`}
                />
                <FieldError message={errors.model} />
              </label>

              <label className={ClassName.field}>
                <span>Year *</span>
                <input
                  inputMode="numeric"
                  value={form.year}
                  onChange={(event) => updateField("year", digitsOnly(event.target.value).slice(0, 4))}
                  placeholder={String(MAX_YEAR)}
                  className={`${ClassName.input} ${errors.year ? ClassName.inputError : ""}`}
                />
                <FieldError message={errors.year} />
              </label>

              <label className={ClassName.field}>
                <span>Price (Birr) *</span>
                <input
                  inputMode="numeric"
                  value={form.price}
                  onChange={(event) => updateField("price", digitsOnly(event.target.value))}
                  placeholder="4,500,000"
                  className={`${ClassName.input} ${errors.price ? ClassName.inputError : ""}`}
                />
                <FieldError message={errors.price} />
              </label>

              <label className={ClassName.field}>
                <span>Fuel type *</span>
                <select
                  value={form.fuelType}
                  onChange={(event) => updateField("fuelType", event.target.value)}
                  className={`${ClassName.input} ${errors.fuelType ? ClassName.inputError : ""}`}
                >
                  <option value="">Select fuel</option>
                  {form.fuelType && !FUEL_OPTIONS.includes(form.fuelType) ? (
                    <option value={form.fuelType}>{form.fuelType}</option>
                  ) : null}
                  {FUEL_OPTIONS.map((fuel) => (
                    <option key={fuel} value={fuel}>{fuel[0].toUpperCase() + fuel.slice(1)}</option>
                  ))}
                </select>
                <FieldError message={errors.fuelType} />
              </label>

              {isAdmin && isUsedCar ? (
                <label className={ClassName.field}>
                  <span>Additional brokers (0 = Direct)</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form.brokerCount}
                    onChange={(event) => updateField("brokerCount", digitsOnly(event.target.value))}
                    placeholder="Not set"
                    className={ClassName.input}
                  />
                </label>
              ) : null}

              <label className={ClassName.field}>
                <span>Color</span>
                <input
                  value={form.color}
                  onChange={(event) => updateField("color", event.target.value)}
                  placeholder="e.g. Pearl white"
                  className={ClassName.input}
                />
              </label>
            </div>

            <label className={`${ClassName.field} mt-4`}>
              <span>Description *</span>
              <textarea
                value={form.description}
                onChange={(event) => updateField("description", event.target.value)}
                placeholder="Describe the condition and important features. Do not add contact details or links."
                rows={6}
                maxLength={3000}
                className={`${ClassName.textarea} ${errors.description ? ClassName.inputError : ""}`}
              />
              <div className={ClassName.textareaMeta}>
                <FieldError message={errors.description} />
                <span>{form.description.length}/3000</span>
              </div>
            </label>

            <details className={ClassName.moreDetails}>
              <summary>
                <span>
                  <strong>More details</strong>
                  <small>Mileage, transmission, location, phone, and electric range</small>
                </span>
                <IoChevronDown />
              </summary>
              <div className={ClassName.formGrid}>
                <label className={ClassName.field}>
                  <span>Mileage (km)</span>
                  <input
                    inputMode="numeric"
                    value={form.mileage}
                    onChange={(event) => updateField("mileage", digitsOnly(event.target.value))}
                    placeholder="Optional"
                    className={ClassName.input}
                  />
                </label>
                <label className={ClassName.field}>
                  <span>Car type</span>
                  <select
                    value={form.bodyType || ""}
                    onChange={(event) => updateField("bodyType", event.target.value)}
                    className={ClassName.input}
                  >
                    <option value="">Auto-detect from model</option>
                    <option value="suv">SUV</option>
                    <option value="sedan">Sedan (Sudan)</option>
                    <option value="minivan">Minivan / Minibus</option>
                    <option value="pickup">Pickup</option>
                    <option value="bus">Bus</option>
                    <option value="truck">Truck</option>
                  </select>
                </label>
                <label className={ClassName.field}>
                  <span>Transmission</span>
                  <select
                    value={form.transmissionType}
                    onChange={(event) => updateField("transmissionType", event.target.value)}
                    className={ClassName.input}
                  >
                    <option value="">Not specified</option>
                    {form.transmissionType && !TRANSMISSION_OPTIONS.includes(form.transmissionType) ? (
                      <option value={form.transmissionType}>{form.transmissionType}</option>
                    ) : null}
                    {TRANSMISSION_OPTIONS.map((value) => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                  </select>
                </label>
                <label className={ClassName.field}>
                  <span>Location</span>
                  <input
                    value={form.location}
                    onChange={(event) => updateField("location", event.target.value)}
                    placeholder="Addis Ababa"
                    className={ClassName.input}
                  />
                </label>
                {isAdmin ? (
                <label className={ClassName.field}>
                  <span>Owner phone (private)</span>
                  <input
                    type="tel"
                    value={form.ownerPhone}
                    onChange={(event) => updateField("ownerPhone", event.target.value.replace(/[^\d+\s()-]/g, ""))}
                    placeholder="Optional, visible only to admins"
                    className={`${ClassName.input} ${errors.ownerPhone ? ClassName.inputError : ""}`}
                  />
                  <FieldError message={errors.ownerPhone} />
                </label>
                ) : null}
                {isElectric ? (
                  <label className={ClassName.field}>
                    <span>Electric range (km)</span>
                    <input
                      inputMode="numeric"
                      value={form.batteryRangeKm}
                      onChange={(event) => updateField("batteryRangeKm", digitsOnly(event.target.value))}
                      placeholder="e.g. 420"
                      className={`${ClassName.input} ${errors.batteryRangeKm ? ClassName.inputError : ""}`}
                    />
                    <FieldError message={errors.batteryRangeKm} />
                  </label>
                ) : null}
              </div>
            </details>
          </section>

          {isUsedCar ? <InspectionEditor carId={String(activeCar._id)} /> : null}

          <section className={ClassName.dangerCard}>
            <div>
              <span className={ClassName.eyebrow}>DANGER ZONE</span>
              <h2 className="mt-1 text-lg font-bold">Delete listing</h2>
              <p className={ClassName.sectionHelper}>This permanently removes the post and uploaded listing photos.</p>
            </div>
            <button
              type="button"
              className={ClassName.deleteButton}
              disabled={deleting}
              onClick={deleteListing}
            >
              <IoTrashOutline /> {deleting ? "Deleting..." : "Delete listing"}
            </button>
          </section>
        </div>

        <aside className={ClassName.sidebar}>
          <section className={ClassName.sideCard}>
            <span className={ClassName.eyebrow}>LISTING MANAGEMENT</span>
            <h2 className={ClassName.sideTitle}>Status and assignment</h2>

            <label className={ClassName.field}>
              <span>Status</span>
              <select value={form.status} onChange={(event) => updateField("status", event.target.value)} className={ClassName.input}>
                {form.status && !STATUS_OPTIONS.includes(form.status) ? <option>{form.status}</option> : null}
                {STATUS_OPTIONS.map((status) => <option key={status}>{status}</option>)}
              </select>
            </label>
            {form.status === "Sold" ? (
              <div className={ClassName.saleOutcome}>
                <strong>Completed sale</strong>
                <span>Record what the buyer actually paid. This improves future price estimates.</span>
                <label className={ClassName.field}>
                  <span>Final selling price (Birr) *</span>
                  <input
                    inputMode="numeric"
                    value={form.actualSalePrice}
                    onChange={(event) => updateField("actualSalePrice", digitsOnly(event.target.value))}
                    placeholder="Actual amount paid"
                    className={`${ClassName.input} ${errors.actualSalePrice ? ClassName.inputError : ""}`}
                  />
                  <FieldError message={errors.actualSalePrice} />
                </label>
                <label className={ClassName.field}>
                  <span>Sold date *</span>
                  <input
                    type="date"
                    value={form.actualSoldAt}
                    max={new Date().toISOString().slice(0, 10)}
                    onChange={(event) => updateField("actualSoldAt", event.target.value)}
                    className={`${ClassName.input} ${errors.actualSoldAt ? ClassName.inputError : ""}`}
                  />
                  <FieldError message={errors.actualSoldAt} />
                </label>
                <label className={ClassName.verificationCheck}>
                  <input
                    type="checkbox"
                    checked={form.outcomeVerified}
                    onChange={(event) => updateField("outcomeVerified", event.target.checked)}
                  />
                  <span><strong>Verified by BossCar</strong><small>Check only after staff confirms the final price with the buyer or seller.</small></span>
                </label>
              </div>
            ) : null}
            <label className={ClassName.field}>
              <span>Assigned agent</span>
              <select value={form.assigned} onChange={(event) => updateField("assigned", event.target.value)} className={ClassName.input}>
                <option value="">Unassigned</option>
                {form.assigned && !agents.some((agent) => agent._id === form.assigned) ? (
                  <option value={form.assigned}>Current agent</option>
                ) : null}
                {agents.map((agent) => <option key={agent._id} value={agent._id}>{agent.name}</option>)}
              </select>
            </label>

            <button
              type="button"
              className={`${ClassName.switchRow} ${form.isDeal ? ClassName.switchActive : ""}`}
              onClick={() => updateField("isDeal", !form.isDeal)}
            >
              <span>
                <strong>Discount deal</strong>
                <small>{form.isDeal ? "Visible in Deals for 48 hours" : "Normal marketplace listing"}</small>
              </span>
              <span className={ClassName.switchKnob} />
            </button>
          </section>

          <section className={ClassName.sideCard}>
            <span className={ClassName.eyebrow}>BUYER FOLLOW-UP</span>
            <h2 className={ClassName.sideTitle}>Order details</h2>
            <label className={ClassName.field}>
              <span>Buyer name</span>
              <input value={form.buyerName} onChange={(event) => updateField("buyerName", event.target.value)} placeholder="Optional" className={ClassName.input} />
            </label>
            <label className={ClassName.field}>
              <span>Buyer phone</span>
              <input type="tel" value={form.buyerPhone} onChange={(event) => updateField("buyerPhone", event.target.value.replace(/[^\d+\s()-]/g, ""))} placeholder="Optional" className={`${ClassName.input} ${errors.buyerPhone ? ClassName.inputError : ""}`} />
              <FieldError message={errors.buyerPhone} />
            </label>
          </section>

          <section className={ClassName.sideCard}>
            <span className={ClassName.eyebrow}>POSTED BY</span>
            <button
              type="button"
              className={ClassName.ownerButton}
              onClick={() => owner?._id && navigate(`${Paths.users}/${owner._id}`)}
            >
              <span className={ClassName.avatar}><IoPersonOutline size={21} /></span>
              <span>
                <strong>{owner?.name || "Unknown user"}</strong>
                <small>{isAdmin ? "Uploader account" : "View account"}</small>
              </span>
              <IoOpenOutline />
            </button>
            {isAdmin ? (
              <div className="mt-3 rounded-xl bg-slate-50 p-3">
                <div className="mb-2 text-xs text-slate-500">Owner phone · Admin only</div>
                {activeCar.ownerPhone ? (
                  <a href={`tel:${activeCar.ownerPhone.replace(/[^\d+]/g, "")}`} className={ClassName.phoneLink}>
                    <IoCallOutline /> {activeCar.ownerPhone}
                  </a>
                ) : <span className="text-sm text-slate-600">Owner number not recorded</span>}
              </div>
            ) : null}
            <div className={ClassName.metaList}>
              <span><small>Brand</small><strong>{selectedMake?.name || makeName(activeCar.make) || "Unknown"}</strong></span>
              <span><small>Posted</small><strong>{formatDate(activeCar.postedAt || activeCar.createdAt)}</strong></span>
              <span><small>Expires</small><strong>{formatDate(activeCar.expiresAt)}</strong></span>
            </div>
          </section>

          {source ? (
            <section className={ClassName.sourceCard}>
              <span className={ClassName.eyebrow}>IMPORTED SOURCE</span>
              <h2 className={ClassName.sideTitle}>{source.provider} contact</h2>
              <strong>{source.sellerName || "Source seller"}</strong>
              <a href={source.sellerPhone ? `tel:${source.sellerPhone}` : undefined} className={ClassName.phoneLink}>
                <IoCallOutline /> {source.sellerPhone || "Phone unavailable"}
              </a>
              <a href={source.url} target="_blank" rel="noreferrer" className={ClassName.sourceLink}>
                Open original post <IoOpenOutline />
              </a>
            </section>
          ) : null}

          {canRepost ? (
            <button
              type="button"
              className={ClassName.repostButton}
              disabled={reposting || repostOnCooldown}
              onClick={repost}
            >
              <IoRefreshOutline />
              <span>
                <strong>{reposting ? "Reposting..." : "Move listing to top"}</strong>
                <small>
                  {repostOnCooldown
                    ? `Available ${new Date(repostAvailableAt).toLocaleString()}`
                    : "Uses the normal repost credit rule"}
                </small>
              </span>
            </button>
          ) : null}
        </aside>
      </main>

      <footer className={ClassName.saveBar}>
        <div>
          {isDirty ? (
            <><IoWarningOutline /><span>You have unsaved changes</span></>
          ) : (
            <><IoCheckmarkCircle /><span>All changes are saved</span></>
          )}
        </div>
        <button type="button" className={ClassName.saveButton} disabled={saving || !isDirty} onClick={save}>
          <IoSaveOutline size={19} /> {saving ? "Saving..." : "Save changes"}
        </button>
      </footer>
    </div>
  );
}

export default CarPage;

const ClassName = {
  page: "pb-28 text-slate-950",
  loadingShell: "mx-auto flex min-h-[70vh] w-full max-w-xl flex-col items-center justify-center gap-4 text-center text-slate-600",
  pageHeader: "mx-auto flex w-full max-w-[1440px] items-start gap-3 border-b border-slate-200 pb-6 pt-2 sm:items-center",
  backButton: "flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-100",
  headerMeta: "flex flex-wrap items-center gap-2 text-xs font-bold tracking-wide text-slate-500 [&>span:first-child]:text-primary",
  statusBadge: "rounded-full bg-slate-100 px-2.5 py-1 text-slate-700",
  unsavedBadge: "rounded-full bg-amber-50 px-2.5 py-1 text-amber-700",
  pageTitle: "mt-1 truncate text-2xl font-bold tracking-tight sm:text-3xl",
  pageSubtitle: "mt-1 hidden text-sm text-slate-500 sm:block",
  headerSave: "hidden h-11 shrink-0 items-center gap-2 rounded-xl bg-slate-950 px-5 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-35 sm:flex",
  headerActions: "hidden shrink-0 items-center gap-2 sm:flex",
  distributeButton: "flex h-11 shrink-0 items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-bold text-slate-700 hover:border-slate-500 hover:text-slate-950",
  errorSummary: "mx-auto mt-4 flex w-full max-w-[1440px] items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700",
  layout: "mx-auto mt-5 grid w-full max-w-[1440px] grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_360px]",
  mainColumn: "flex min-w-0 flex-col gap-5",
  sidebar: "flex min-w-0 flex-col gap-4 xl:sticky xl:top-5",
  card: "rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm sm:p-6",
  sideCard: "flex flex-col gap-4 rounded-[22px] border border-slate-200 bg-white p-5 shadow-sm",
  saleOutcome: "flex flex-col gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 [&>strong]:text-sm [&>span]:text-xs [&>span]:leading-5 [&>span]:text-emerald-800",
  verificationCheck: "flex cursor-pointer items-start gap-3 rounded-xl border border-emerald-200 bg-white p-3 [&>input]:mt-1 [&>span]:flex [&>span]:flex-col [&_strong]:text-sm [&_small]:mt-1 [&_small]:text-xs [&_small]:font-normal [&_small]:leading-4 [&_small]:text-slate-500",
  sourceCard: "flex flex-col gap-3 rounded-[22px] border border-red-100 bg-red-50/60 p-5",
  dangerCard: "flex flex-col justify-between gap-5 rounded-[24px] border border-red-200 bg-white p-5 sm:flex-row sm:items-center sm:p-6",
  sectionHeader: "flex items-start justify-between gap-4",
  eyebrow: "text-[10px] font-bold tracking-[0.2em] text-primary",
  sectionTitle: "mt-1 text-xl font-bold",
  sideTitle: "-mt-2 text-lg font-bold",
  sectionHelper: "mt-1 text-sm leading-5 text-slate-500",
  segmented: "mt-5 grid grid-cols-2 rounded-2xl bg-slate-100 p-1",
  segment: "h-11 rounded-xl text-sm font-bold text-slate-500 hover:text-slate-900",
  segmentActive: "h-11 rounded-xl bg-slate-950 text-sm font-bold text-white shadow-sm",
  conditionRow: "mt-3 grid grid-cols-2 gap-3",
  condition: "flex h-12 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-sm font-bold text-slate-500",
  conditionActive: "flex h-12 items-center justify-center gap-2 rounded-xl border border-primary bg-red-50 text-sm font-bold text-primary",
  driverRow: "mt-4 flex flex-col justify-between gap-3 rounded-2xl border border-slate-200 p-4 sm:flex-row sm:items-center [&>div:first-child]:flex [&>div:first-child]:flex-col [&_small]:text-xs [&_small]:font-normal [&_small]:text-slate-500",
  miniSegmented: "grid grid-cols-2 rounded-xl bg-slate-100 p-1",
  miniButton: "h-9 rounded-lg px-3 text-xs font-bold text-slate-500",
  miniActive: "h-9 rounded-lg bg-white px-3 text-xs font-bold text-slate-950 shadow-sm",
  formGrid: "mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3",
  field: "flex min-w-0 flex-col gap-2 text-sm font-semibold text-slate-700",
  input: "h-12 w-full rounded-xl border border-slate-200 bg-white px-3.5 font-normal text-slate-950 outline-none transition-colors placeholder:text-slate-400 focus:border-slate-950",
  textarea: "w-full resize-y rounded-2xl border border-slate-200 bg-white p-4 font-normal leading-6 text-slate-950 outline-none placeholder:text-slate-400 focus:border-slate-950",
  inputError: "border-red-400 bg-red-50/30",
  fieldError: "text-xs font-medium leading-4 text-red-600",
  textareaMeta: "flex min-h-4 justify-between gap-3 text-xs font-normal text-slate-400 [&>span:first-child]:flex-1",
  moreDetails: "group mt-5 rounded-2xl border border-slate-200 bg-slate-50/70 p-1 open:bg-slate-50 [&>summary]:flex [&>summary]:cursor-pointer [&>summary]:list-none [&>summary]:items-center [&>summary]:justify-between [&>summary]:gap-3 [&>summary]:rounded-xl [&>summary]:bg-white [&>summary]:px-4 [&>summary]:py-3 [&>summary>span]:flex [&>summary>span]:flex-col [&>summary_small]:font-normal [&>summary_small]:text-slate-500 [&>summary>svg]:transition-transform open:[&>summary>svg]:rotate-180 [&>div]:px-3 [&>div]:pb-4",
  switchRow: "flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-left [&>span:first-child]:flex [&>span:first-child]:flex-col [&_small]:text-xs [&_small]:font-normal [&_small]:text-slate-500 [&>span:last-child]:relative [&>span:last-child]:h-6 [&>span:last-child]:w-11 [&>span:last-child]:rounded-full [&>span:last-child]:bg-slate-300 [&>span:last-child]:before:absolute [&>span:last-child]:before:left-1 [&>span:last-child]:before:top-1 [&>span:last-child]:before:h-4 [&>span:last-child]:before:w-4 [&>span:last-child]:before:rounded-full [&>span:last-child]:before:bg-white [&>span:last-child]:before:transition-transform",
  switchActive: "border-red-200 bg-red-50 [&>span:last-child]:bg-primary [&>span:last-child]:before:translate-x-5",
  switchKnob: "shrink-0",
  ownerButton: "flex items-center gap-3 rounded-2xl bg-slate-50 p-3 text-left hover:bg-slate-100 [&>span:nth-child(2)]:flex [&>span:nth-child(2)]:min-w-0 [&>span:nth-child(2)]:flex-1 [&>span:nth-child(2)]:flex-col [&_strong]:truncate [&_small]:truncate [&_small]:text-xs [&_small]:font-normal [&_small]:text-slate-500",
  avatar: "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-slate-500 shadow-sm",
  metaList: "grid grid-cols-1 gap-2 border-t border-slate-100 pt-3 [&>span]:flex [&>span]:items-center [&>span]:justify-between [&>span]:gap-3 [&_small]:text-slate-500 [&_strong]:text-right [&_strong]:text-xs",
  phoneLink: "inline-flex items-center gap-2 text-sm font-bold text-slate-800",
  sourceLink: "inline-flex items-center gap-2 text-sm font-bold text-primary",
  repostButton: "flex items-center gap-3 rounded-[20px] border border-slate-200 bg-white p-4 text-left shadow-sm hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-45 [&>svg]:text-primary [&>span]:flex [&>span]:flex-col [&_small]:text-xs [&_small]:font-normal [&_small]:text-slate-500",
  deleteButton: "inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-5 text-sm font-bold text-red-600 hover:bg-red-100 disabled:opacity-50",
  saveBar: "fixed bottom-3 left-1/2 z-40 flex w-[calc(100%-24px)] max-w-2xl -translate-x-1/2 items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-2xl backdrop-blur xl:left-[calc(50%+9rem)] [&>div]:hidden [&>div]:items-center [&>div]:gap-2 [&>div]:text-sm [&>div]:font-semibold [&>div]:text-slate-500 sm:[&>div]:flex",
  saveButton: "inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-35 sm:w-auto",
  darkButton: "mt-2 rounded-xl bg-slate-950 px-5 py-3 text-sm font-bold text-white",
};



